import type { ItineraryUpdate, StopVisitRole, StreamingStopVisit, TripItineraryData } from 'tripPlanner/api/types'

export interface SelectedDayContext {
  tripStopId: number
  startDayId: number
  endDayId?: number
  startDayNumber: number
  endDayNumber?: number
}

interface PendingExperienceDaySelectionOptions {
  offerId?: string
  offerType?: string
  selectedDayContext?: SelectedDayContext
}

interface PendingExperienceDaySelection extends SelectedDayContext {
  offerId: string
  selectedAt: number
  itemId?: number
}

const DEFAULT_STOP_VISIT_ROLE: StopVisitRole = 'ACTIVITY'
const PENDING_SELECTION_TTL_MS = 30_000
const pendingSelections = new Map<string, PendingExperienceDaySelection>()

function getEffectiveEndDayId(selection: SelectedDayContext): number {
  return selection.endDayId ?? selection.startDayId
}

function getEffectiveEndDayNumber(selection: SelectedDayContext): number {
  return selection.endDayNumber ?? selection.startDayNumber
}

function isSelectionFresh(selection: PendingExperienceDaySelection, now = Date.now()): boolean {
  return now - selection.selectedAt <= PENDING_SELECTION_TTL_MS
}

function isExperienceItemUpdate(
  update: ItineraryUpdate,
): update is Extract<ItineraryUpdate, { kind: 'item_added' | 'item_updated' }> {
  return update.kind === 'item_added' || update.kind === 'item_updated'
}

function hasUsableDayAssignment(stopVisits: Array<StreamingStopVisit>): boolean {
  return stopVisits.some(stopVisit =>
    (stopVisit.startDayNumber != null && stopVisit.endDayNumber != null) ||
    stopVisit.startDayId != null ||
    stopVisit.endDayId != null,
  )
}

function getExperienceOfferId(update: Extract<ItineraryUpdate, { kind: 'item_added' | 'item_updated' }>): string | undefined {
  const { savedItemData } = update.item

  if (savedItemData?.source !== 'offer_experience') {
    return undefined
  }

  return savedItemData.experienceId
}

function isSelectionUsableForUpdate(
  selection: PendingExperienceDaySelection,
  stopVisits: Array<StreamingStopVisit>,
): boolean {
  if (stopVisits.length === 0) {
    return true
  }

  return stopVisits.every(stopVisit => stopVisit.tripStopId === selection.tripStopId)
}

function isSelectionStillValid(
  itineraryData: TripItineraryData | undefined,
  selection: PendingExperienceDaySelection,
): boolean {
  const stop = itineraryData?.stops.find(({ id }) => id === selection.tripStopId)

  if (!stop) {
    return false
  }

  return stop.days.some(day =>
    day.id === selection.startDayId &&
    day.dayNumber === selection.startDayNumber,
  )
}

function createStopVisitFromSelection(
  stopVisits: Array<StreamingStopVisit>,
  selection: PendingExperienceDaySelection,
): StreamingStopVisit {
  return {
    tripStopId: selection.tripStopId,
    startDayId: selection.startDayId,
    endDayId: getEffectiveEndDayId(selection),
    startDayNumber: selection.startDayNumber,
    endDayNumber: getEffectiveEndDayNumber(selection),
    role: stopVisits[0]?.role ?? DEFAULT_STOP_VISIT_ROLE,
  }
}

function pruneExpiredSelections(now = Date.now()): void {
  for (const [offerId, selection] of pendingSelections.entries()) {
    if (!isSelectionFresh(selection, now)) {
      pendingSelections.delete(offerId)
    }
  }
}

export function storePendingExperienceDaySelection(
  offerId: string,
  selection: SelectedDayContext,
): void {
  pendingSelections.set(offerId, {
    offerId,
    ...selection,
    selectedAt: Date.now(),
  })
}

export function clearPendingExperienceDaySelection(offerId: string): void {
  pendingSelections.delete(offerId)
}

export function resetPendingExperienceDaySelections(): void {
  pendingSelections.clear()
}

export function syncPendingExperienceDaySelection(
  options?: PendingExperienceDaySelectionOptions,
): void {
  if (options?.offerType !== 'experience' || !options.offerId) {
    return
  }

  if (options.selectedDayContext) {
    storePendingExperienceDaySelection(
      options.offerId,
      options.selectedDayContext,
    )
    return
  }

  clearPendingExperienceDaySelection(options.offerId)
}

export function patchExperienceUpdatesWithPendingDaySelection(
  itineraryData: TripItineraryData | undefined,
  updates: Array<ItineraryUpdate>,
): Array<ItineraryUpdate> {
  pruneExpiredSelections()

  return updates.map(update => {
    if (!isExperienceItemUpdate(update)) {
      return update
    }

    if (hasUsableDayAssignment(update.stopVisits)) {
      return update
    }

    const offerId = getExperienceOfferId(update)
    if (!offerId) {
      return update
    }

    const selection = pendingSelections.get(offerId)
    if (!selection || !isSelectionFresh(selection)) {
      return update
    }

    if (selection.itemId !== undefined && selection.itemId !== update.itemId) {
      return update
    }

    if (!isSelectionUsableForUpdate(selection, update.stopVisits)) {
      return update
    }

    if (!isSelectionStillValid(itineraryData, selection)) {
      pendingSelections.delete(offerId)
      return update
    }

    pendingSelections.set(offerId, {
      ...selection,
      itemId: update.itemId,
    })

    return {
      ...update,
      stopVisits: [createStopVisitFromSelection(update.stopVisits, selection)],
    }
  })
}

export function resetPendingExperienceDaySelectionsForTests(): void {
  resetPendingExperienceDaySelections()
}
